// **Lizenzen** — Vorlagen für Benutzer-Lizenzen (docs/11 8e): was ein Benutzer
// mit der Lizenz darf und was sie je Benutzer und Monat kostet (EUR). Hier das
// Reine: die Rechte, ihre Zusammenfassung und die Umwandlung zwischen Vorlage,
// Formularzustand und Rumpf — getestet (license-model.test.ts).

import type { SeatRights } from "../../api/types";

export type RightFlag = "chat" | "agents" | "code" | "tasks" | "ai_call" | "stt" | "tts" | "routines" | "custom_mcp";

export const RIGHTS: ReadonlyArray<readonly [RightFlag, string, string]> = [
  ["chat", "Chat", "allgemeine Unterhaltungen"],
  ["agents", "Agents", "eigene Assistenten bauen und nutzen"],
  ["code", "Code", "Arbeitsbereiche (Projekte mit Repository)"],
  ["tasks", "Geplante Aufgaben", "wiederkehrende ./run.sh-Läufe"],
  ["ai_call", "AI-Call", "mit dem Agenten telefonieren"],
  ["stt", "Diktieren (STT)", "Sprache zu Text im Eingabefeld"],
  ["tts", "Vorlesen (TTS)", "Antworten vorlesen lassen"],
  ["routines", "Routinen", "wiederkehrende Jobs im Chat"],
  ["custom_mcp", "Eigene MCP-Server", "eigene Werkzeuge anbinden"],
];

export const LICENSE_ERRORS: Record<string, string> = {
  invalid_key: "Kennung: Kleinbuchstaben, Ziffern, - und _ (max. 63 Zeichen), beginnt mit Buchstabe oder Ziffer.",
  key_taken: "Diese Kennung gibt es schon.",
  invalid_name: "Ein Name ist Pflicht.",
  invalid_price: "Ungültiger Preis.",
  template_in_use: "Mandanten nutzen diese Lizenz noch — erst dort die Plätze entfernen.",
};

/** „Chat, Agents, Code · 2 gleichzeitig" */
export function rightsSummary(r: SeatRights): string {
  const parts = RIGHTS.filter(([k]) => r[k]).map(([k, l]) => (k === "routines" && r.max_routines !== null ? `${l} (max. ${r.max_routines})` : l));
  return `${parts.length ? parts.join(", ") : "nichts"}${r.max_parallel ? ` · ${r.max_parallel} gleichzeitig` : " · beliebig viele gleichzeitig"}`;
}

/** Das Formular: Texte als Zeichenketten (wie im Feld), Rechte als Schalter. */
export interface LicenseFormState {
  key: string;
  name: string;
  description: string;
  price: string;
  flags: Record<RightFlag, boolean>;
  max_parallel: string;
  max_routines: string;
}

/** Die Vorgabe einer neuen Lizenz: Chat, Diktieren, Vorlesen, zwei gleichzeitig. */
export const NEW_RIGHTS: SeatRights = {
  chat: true, agents: false, code: false, tasks: false, ai_call: false, stt: true, tts: true,
  routines: false, max_routines: null, custom_mcp: false, max_parallel: 2,
};

const str = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v));

export function fromTemplate(tpl: { key: string; name: string; description: string; price_eur_month: number | null; rights: SeatRights }): LicenseFormState {
  const flags = Object.fromEntries(RIGHTS.map(([k]) => [k, Boolean(tpl.rights[k])])) as Record<RightFlag, boolean>;
  return {
    key: tpl.key,
    name: tpl.name,
    description: tpl.description,
    price: str(tpl.price_eur_month),
    flags,
    max_parallel: str(tpl.rights.max_parallel),
    max_routines: str(tpl.rights.max_routines),
  };
}

export interface LicenseBody {
  name: string;
  description: string;
  price_eur_month: number | null;
  rights: SeatRights;
  key?: string;
}

/** Leer → `null`, sonst die Zahl — dieselbe Regel für Preis und Grenzen. */
const num = (s: string): number | null => (s.trim() === "" ? null : Number(s.trim()));

/**
 * Der Rumpf für `POST`/`PUT /license-templates`. Alle Texte getrimmt; die
 * Kennung nur beim Anlegen (danach ist sie fest) und kleingeschrieben.
 */
export function toBody(s: LicenseFormState, isNew: boolean): LicenseBody {
  const rights: SeatRights = { ...s.flags, max_parallel: num(s.max_parallel), max_routines: num(s.max_routines) };
  const body: LicenseBody = { name: s.name.trim(), description: s.description.trim(), price_eur_month: num(s.price), rights };
  if (isNew) body.key = s.key.trim().toLowerCase();
  return body;
}

/** Die Meldung nach dem Speichern einer geänderten Vorlage (sie geht sofort an die Mandanten). */
export function savedMessage(res: { pushed: string[]; failed: string[] }): [string, "" | "error"] {
  if (res.failed.length) return [`Gespeichert — ${res.failed.length} Mandant(en) nicht erreicht, dort beim nächsten „Übernehmen"`, "error"];
  return [res.pushed.length ? `Gespeichert und an ${res.pushed.length} Mandant(en) übertragen` : "Gespeichert", ""];
}
